import { useMemo } from "react";
import { DataExplorerContext, DataExplorerContextV2 } from "@depmap/types";
import {
  isNegatedContext,
  loadContextsFromLocalStorage,
} from "../../utils/context";
import { isContextAll } from "../../utils/contextExpr";
import {
  getExpansionRoutes,
  getRouteHeading,
} from "../../utils/expansionRoutes";
import useContextHash from "./useContextHash";

const EMPTY_OBJECT = {};

const compareContextEntries = (
  a: [string, { name: string }],
  b: [string, { name: string }]
) => (a[1].name.toLowerCase() < b[1].name.toLowerCase() ? -1 : 1);

// Sentinel prefix for the "children of a parent entity" quick-picks. Parallel
// to "all"/"new"/"edit"/"manage": the value is not a context hash, and
// useChangeHandler switches on it rather than trying to load anything. The
// suffix names the route's parent type, since a child type may have more than
// one (see getExpansionRoutes).
export const PARENT_PICK_PREFIX = "parent_pick_";

export default function useOptions(
  value: DataExplorerContext | DataExplorerContextV2 | null,
  dimension_type: string,
  includeAllInOptions: boolean,
  linkToContextManager: boolean
) {
  const loadedContexts: Record<string, DataExplorerContextV2> = dimension_type
    ? loadContextsFromLocalStorage(dimension_type)
    : EMPTY_OBJECT;

  const userContextOptions = useMemo(() => {
    return Object.entries(loadedContexts)
      .sort(compareContextEntries)
      .map(([hash, context]) => {
        return {
          label: context.name,
          value: hash,
          isLegacyList: Boolean(
            "isLegacyList" in context && context.isLegacyList
          ),
        };
      });
  }, [loadedContexts]);

  const outGroupOptions = useMemo(() => {
    return Object.entries(loadedContexts)
      .sort(compareContextEntries)
      .map(([hash, context]) => {
        return {
          label: `Not ${context.name}`,
          // HACK: These hashes can have a "not_" prefix that's a total hack
          // to encode that they should be part of the outgroup options. It's
          // important that these hashes do not leak out into the rest of the
          // system. A proper solution would be to use an object with an
          // { outgroup: true } property as a value for the  dropdowns.
          value: `not_${hash}`,
          isLegacyList: Boolean(
            "isLegacyList" in context && context.isLegacyList
          ),
        };
      });
  }, [loadedContexts]);

  const {
    isKnownContext,
    hashOfSelectedValue,
    shouldShowSaveButton,
  } = useContextHash(value as DataExplorerContextV2, dimension_type);

  const unsavedOptions = useMemo(() => {
    if (
      value &&
      hashOfSelectedValue &&
      !isKnownContext &&
      !isContextAll(value)
    ) {
      return [
        {
          label: value.name,
          value: isNegatedContext(value)
            ? `not_${hashOfSelectedValue}`
            : hashOfSelectedValue,
        },
      ];
    }

    return null;
  }, [value, hashOfSelectedValue, isKnownContext]);

  // "Transcripts of a gene…" and friends. Offered ahead of "New" because for
  // the types that have a route it is almost always what the user wants, and
  // it saves them hand-building a single-equality context in the builder.
  const parentPickOptions = useMemo(() => {
    return getExpansionRoutes(dimension_type).map((route) => ({
      label: `${getRouteHeading(dimension_type, route)}…`,
      value: `${PARENT_PICK_PREFIX}${route.parentType}`,
    }));
  }, [dimension_type]);

  return useMemo(() => {
    const specialOptions = [
      includeAllInOptions ? { label: "All", value: "all" } : null,

      ...parentPickOptions,

      { label: "New", value: "new" },

      value && hashOfSelectedValue !== "all" && !shouldShowSaveButton
        ? { label: "Edit current…", value: "edit" }
        : null,

      linkToContextManager && Object.keys(loadedContexts).length > 0
        ? { label: "Manage my contexts…", value: "manage" }
        : null,

      unsavedOptions
        ? { label: "Unsaved Contexts", options: unsavedOptions }
        : null,
    ].filter(Boolean);

    const contexts = [
      { label: "My Contexts", options: userContextOptions },
      { label: "Out Groups", options: outGroupOptions },
    ];

    return [...(specialOptions as typeof contexts), ...contexts];
  }, [
    hashOfSelectedValue,
    includeAllInOptions,
    linkToContextManager,
    loadedContexts,
    outGroupOptions,
    parentPickOptions,
    shouldShowSaveButton,
    unsavedOptions,
    userContextOptions,
    value,
  ]);
}
